import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch, message } from 'antd';
import {
  getAMLSCommonAlertPartyIdentity,
  getAMLSCommonAlertDetails,
  putAMLSCommonAlertPartyUpdate
} from '../../../../redux/actions/common';
import { isResourceAvailable } from '../../../../util';
import { getSelectedResources } from '../../../../util/resourceManager';
import SmartAssessment from '../SmartAssessment';
import AlertHeaderCard from './components/AlertHeaderCard';
import FpAlertHeader from './components/FpAlertHeader';
import EventTimeline from './components/EventTimeline';
import EventDetailPanel from './components/EventDetailPanel';
import AlertTabs from './components/AlertTabs';
import { alertDetailMock } from './mocks/alertDetailMock';
import { isMockFallbackEnabled } from './framlLayoutToggle';
import style from './index.scss';

const { trmResources } = getSelectedResources();

// The Run Instance listing, on Prevention's RESULTS view — the tab that lists the
// alerts this detail page is opened from.
//
// BOTH PARAMETERS ARE REQUIRED, and shipping only one was the bug review caught:
//   ?tab=2       selects the OUTER Prevention tab. The dashboard reads
//                `query.get('tab') || '1'`, and '1' is Detection — so a user who
//                also holds Detection access landed there and never saw the
//                Results list. Prevention-only users never noticed, which is why
//                it passed every test I wrote.
//   ?view=results selects Prevention's INNER Results tab, read by
//                testRunInstanceDashboard/Prevention. Without it the page opens
//                on Summary.
//
// Classic's RETURN_TO_PATH carried the opposite half — `?tab=2` with no view — so
// the two views sent the user to different places. Both now use the same string.
const ALERTS_LISTING_ROUTE = '/app/trm/run-instance?tab=2&view=results';

// FIN-13619 — the view the user last picked, kept in localStorage keyed by userName (set by
// keycloak.js / Login.js) so it survives a remount (bell-notification click) and a refresh, and
// two analysts on one workstation don't inherit each other's choice. Storage can be blocked, so
// every access is guarded and a failure just falls back to the configured default.
// ponytail: per browser, not per user everywhere; PRD 3122987032 §9 fully needs a preference API.
const viewModeKey = () =>
  `framl-alert-view-mode:${localStorage.getItem('userName') || ''}`;
const readViewMode = () => {
  try {
    const saved = localStorage.getItem(viewModeKey());
    return saved === 'classic' || saved === 'spatial' ? saved : undefined;
  } catch (e) {
    return undefined;
  }
};
const saveViewMode = (mode) => {
  try {
    localStorage.setItem(viewModeKey(), mode);
  } catch (e) {
    // Blocked storage only costs the memory of the choice.
  }
};

// FIN-13619 — Classic tab keys (SmartAssessment validTabs) to Spatial AlertTabs keys, so a deep
// link such as ?tab=downloadHistory opens the same tab in either view. Classic '1' (General
// Information) has no Spatial counterpart and falls through to AlertTabs' own default.
const CLASSIC_TO_SPATIAL_TAB = {
  2: 'transactions',
  3: 'activity',
  4: 'explanation',
  downloadHistory: 'downloadHistory'
};

// FIN-11304 — FRAML-P Alert Detail redesign ("Spatial View").
// Mounted in parallel to the legacy SmartAssessment via FramlAlertDetailRouter.
//
// Built module-by-module (top of the design downward):
//   [x] Module 1 — action row (Add Note + Classic/Spatial toggle) + header card
//   [ ] Module 2 — event timeline (the spatial centerpiece)
//   [ ] Module 3 — event side panel
//   [ ] Module 4 — tab strip
//   [ ] Module 5 — tab contents (reused tabs, Related Cases, Custom Tables)
//
// Runs on a mock fixture (mock-first) until BE-1 (FIN-11364) lands.

const FramlAlertDetailShell = (props) => {
  const { alertMetaSuffix } = props;
  // Real alertId (CM passes `alertId`; RI route uses match params) — used to
  // wire the reused, self-connected tab components (e.g. ScoreExplainability).
  const alertId = props.match?.params?.alertId || props.alertId;
  // Mirror SmartAssessment's mount behaviour (SmartAssessment.js:80-88). The
  // party identity populates `amlsCommon.alertPartyIdentity`, which is the ONLY
  // source of a customer key for the reused Transactions tab — its container
  // reads redux without consulting ownProps, so passing a prop cannot help it.
  // `isList: true` is what FRAML-P uses (isPrevention), switching the URL to
  // /alerts/{id}/customerlist. Without this the tab never fetches at all.
  useEffect(() => {
    if (alertId) {
      // Populates `amlsCommon.alertDetails` — the source of primaryPartyKey (the
      // FRAML-P envelope), scenarioId and triggeredRules. In Classic this is
      // fetched by the header components (AlertTopPanelDetails /
      // SmartAssessmentPageHeader); the Spatial shell replaced that header with
      // AlertHeaderCard, so nothing fetched it and every party-keyed tab —
      // Transactions, Activity, History — silently skipped its request.
      props.getAlertDetails({ alertId });
      props.getPartyIdentity({ alertId, isList: true });
    }
  }, [alertId]);
  // Mock fixtures are DEV-ONLY. The tabs with live containers gate themselves;
  // these surfaces have no container yet (timeline needs BE-1, Related Cases BE-3,
  // Custom Tables FIN-11586), so gate them here. In a deployed build they render
  // empty rather than showing fabricated events/cases an analyst could act on.
  // The header is no longer among them — it reads the live alertDetails slice.
  const mockAllowed = isMockFallbackEnabled();
  const data = alertDetailMock;
  // Empty shapes chosen to match what each consumer tolerates: EventTimeline
  // guards `sequence?.events`, CustomTablesPanel wants an array.
  const eventSequence = mockAllowed
    ? data.eventSequence
    : { total: 0, events: [] };
  const relatedCases = mockAllowed ? data.relatedCases : undefined;
  const customTables = mockAllowed ? data.customTables : [];

  /* WHICH VIEW THIS OPENS ON, from the router's resolved layout.
     
     The shell is now mounted for EVERY FRAML-P alert (FramlAlertDetailRouter,
     2026-09-17) so the toggle is present in both cases; the flag chooses the
     starting view rather than the component. Defaults to 'spatial' so a direct
     mount — tests, or any caller that does not pass the prop — behaves as before.
     
     A choice the user made before wins over the flag (readViewMode, FIN-13619).
     It is remembered per browser; PRD 3122987032 §9's per-user-everywhere
     memory still needs a preference API, which does not exist yet. */
  const [viewMode, setViewMode] = useState(
    () =>
      readViewMode() ||
      (props.initialViewMode === 'classic' ? 'classic' : 'spatial')
  ); // 'spatial' | 'classic'
  const locationSearch = props.location?.search || window.location.search;
  const spatialTab =
    CLASSIC_TO_SPATIAL_TAB[new URLSearchParams(locationSearch).get('tab')];
  // Only the fallback Status/Assign stubs read this now (they render when CM does
  // not inject alertMetaSuffix). The three displayed fields come from alertDetails.
  const [header, setHeader] = useState(mockAllowed ? data.header : {});
  // Default: no event selected -> panel closed, timeline full width + hint
  // (matches the design's default state).
  const [selectedEventId, setSelectedEventId] = useState(null);
  // Held here rather than derived: the timeline container owns the live events,
  // so it hands the selected one up. Deriving it from the fixture would break
  // the side panel the moment real data arrives.
  const [selectedEvent, setSelectedEvent] = useState(null);

  const isSpatial = viewMode === 'spatial';

  // FIN-13570 — maximise the timeline by giving it the event panel's width.
  const [timelineMaximized, setTimelineMaximized] = useState(false);

  // FIN-13562 — the Alert Quality write.
  //
  // SAVING IS LOCAL STATE, NOT `updateUnifiedAlertStatus.isLoading`, and that is
  // not a style choice: the slice is initialised `isLoading: true`
  // (reducers/common.js:148), so reading it directly leaves the select disabled
  // with a spinner from mount, before anything has been saved. Classic hit the
  // same thing and solved it the same way (SmartAssessmentPageHeader:34) — the
  // flag is raised on change and cleared when a response lands.
  const [savingQuality, setSavingQuality] = useState(false);
  /* Incremented on every failed save. FpAlertHeader holds the analyst's choice
     locally until the store agrees; without this it would keep showing a value
     the backend rejected. A counter rather than a boolean so two consecutive
     failures are two distinct events. */
  const [qualitySaveFailures, setQualitySaveFailures] = useState(0);

  // Same payload shape Classic sends (SmartAssessmentPageHeader:53-55).
  // sourceId is read off the alert rather than assumed: this shell serves
  // source 24 on the run-instance route and 25 from CM, and the write is keyed
  // on it.
  const handleAlertQualityChange = (alertQuality) => {
    setSavingQuality(true);
    props.updateAlertQuality([
      { alertId, sourceId: props.alertDetails?.alertSourceId, alertQuality }
    ]);
  };

  /* Only acts once a save is actually in flight, so the `data: null` the slice
     starts with cannot fire a toast on page load. 202 is the API's accepted
     status.

     WATCHES `error` AS WELL AS `data` (raised in review). Classic's version of
     this effect reads only the data, and on failure the reducer sets
     `{ isLoading: false, data: null, error }` — so the guard above returned
     early, `savingQuality` stayed true, and the select sat disabled behind a
     spinner for the rest of the session with no error ever shown. Copying
     Classic's effect carried that bug in; it is corrected here rather than
     mirrored.

     A STALE ERROR CANNOT FIRE THIS: starting a save resets the slice to
     `{ isLoading: true, error: null, data: null }` (PUT_AMLS_COMMON_ALERT_PARTY_
     UPDATE), so both fields are empty again before the next response lands. */
  const qualityResult = props.updateQualityResult;
  const qualityError = props.updateQualityError;
  useEffect(() => {
    if (!savingQuality) return;
    if (qualityResult == null && qualityError == null) return;

    setSavingQuality(false);

    if (qualityError || qualityResult?.status !== 202) {
      // The chosen value was NOT stored, so the header must stop showing it as
      // though it were — see `qualitySaveFailures`.
      setQualitySaveFailures((n) => n + 1);
      message.error(
        qualityError?.message || qualityResult?.message || 'Fail to update'
      );
      return;
    }

    message.success('Review feedback has been saved successfully');
    /* `savingQuality` IS A DEPENDENCY, not just a guard. It was omitted, so if a
       response was already in the slice when the save started the effect never
       re-ran and the control stayed locked. Safe to depend on: starting a save
       resets both fields to null, so the first run after the flag flips returns
       early. */
  }, [qualityResult, qualityError, savingQuality]);

  // Access parity with Classic (SmartAssessment.js:73-78): the tab strip is
  // gated on the alert-details UI resource, and CM's own route is always
  // allowed. Without this the Spatial view showed all seven tabs to a user who
  // sees none in Classic — an access-control divergence, not a cosmetic one.
  // It does not surface on the CM route (isFromCM short-circuits there), which
  // is why manual testing missed it.
  const viewAccessTabs =
    props.isFromCM ||
    isResourceAvailable(
      props.authentication,
      trmResources.APP_3_RESOURCE_UI_AMLS_TM_ALERT_DETAILS
    );

  return (
    <div className={style.shellContainer}>
      <div className={style.contentWrapper}>
        <div className={style.actionRow}>
          {/* NO ADD NOTE BUTTON. It was drawn in the design and shipped here
              above the isSpatial branch, so it appeared on BOTH views — and it
              never had an onClick, which is what FIN-13555 was raised for.
              Removed rather than wired up, on the product decision that the
              action does not belong on this screen. The action row now holds the
              Classic/Spatial toggle alone. */}
          <div className={style.viewToggle}>
            <span className={!isSpatial ? style.toggleLabelActive : ''}>
              Classic
            </span>
            <Switch
              checked={isSpatial}
              onChange={(checked) => {
                const mode = checked ? 'spatial' : 'classic';
                saveViewMode(mode);
                setViewMode(mode);
              }}
            />
            <span className={isSpatial ? style.toggleLabelActive : ''}>
              Spatial
            </span>
          </div>
        </div>

        {isSpatial ? (
          <>
            {/* Header is Spatial-only. In Classic mode the legacy SmartAssessment
                renders its own (SmartAssessmentTopPanel), so rendering ours there
                would duplicate it.
                TWO HEADERS, ONE PER ROUTE (user, from the designs 2026-08-20):
                  CM -> AlertHeaderCard, a card of Alert ID / Source / Date /
                        Status / Assign to plus the AlertMetrics counters.
                  FP -> FpAlertHeader, a strip led by the score bar with inline
                        transaction facts and no counters.
                Everything BELOW the header — the timeline, the scenario bar with
                its variant/typology/score/action, the tabs — is identical on both
                routes and deliberately not branched.
                `isFromCM` is the discriminator because CM's AlertDetails is the
                only caller that sets it (it also passes a resolved `module`; see
                FramlAlertDetailRouter's mapStateToProps, which keys off the same
                distinction). */}
            {props.isFromCM ? (
              <AlertHeaderCard
                alertDetails={props.alertDetails}
                header={header}
                alertMetaSuffix={alertMetaSuffix}
                onStatusChange={(status) =>
                  setHeader((h) => ({ ...h, status }))
                }
                onAssigneeChange={(assignedTo) =>
                  setHeader((h) => ({ ...h, assignedTo }))
                }
              />
            ) : (
              <FpAlertHeader
                alertDetails={props.alertDetails}
                /* FIN-13562 — value and write both come from the same slice and
                   action Classic's header uses, so the two views cannot
                   disagree about what was saved. */
                alertQuality={
                  // FIN-13754 — a save made in Classic lands here, so the
                  // value survives the toggle without a refresh.
                  props.savedAlertQuality?.[alertId] ??
                  props.alertDetails?.alertQuality
                }
                onAlertQualityChange={handleAlertQualityChange}
                isSavingQuality={savingQuality}
                qualitySaveFailures={qualitySaveFailures}
                /* FIN-13574 — a FIXED destination, and specifically the
                   RESULTS view.
                   goBack() returns to whatever the browser saw last, which is
                   the listing only when the analyst clicked a row to get here.
                   Open an alert by URL — shared, bookmarked, or QA's repro —
                   and the entry behind it is the login redirect, which sends
                   them straight back to the same alert.
                   `?view=results` rather than the bare path: the alerts are
                   under the Results tab, and Summary is what the page opens on
                   otherwise (Prevention/index.js). Landing on an unrelated,
                   empty tab is barely better than not navigating.
                   Optional-chained because the shell is also rendered without a
                   Router in tests. */
                onBack={() => props.history?.push(ALERTS_LISTING_ROUTE)}
              />
            )}
            <div className={style.spatialRow}>
              {/* Timeline is now a container: it fetches its own events from
                  BE-1 and maps them. `fallbackSequence` is the DEV-ONLY fixture,
                  used only while the mock toggle is on. */}
              <EventTimeline
                alertId={alertId}
                fallbackSequence={eventSequence}
                selectedId={selectedEventId}
                onSelect={(id, event) => {
                  setSelectedEventId(id);
                  setSelectedEvent(event);
                }}
                /* FIN-13570 — the maximise toggle is owned HERE, not in the
                   timeline, because maximising has to close the event panel and
                   that panel is the timeline's SIBLING in .spatialRow. Held
                   below, the toggle could only resize nodes inside its own card,
                   which is why it read as doing nothing. */
                expanded={timelineMaximized}
                onExpandedChange={setTimelineMaximized}
              />
              {/* HIDDEN, NOT UNMOUNTED-AND-FORGOTTEN: `selectedEventId` is left
                  alone, so shrinking back restores the same event rather than
                  making the analyst hunt for the node again. */}
              {!timelineMaximized && (
                <EventDetailPanel
                  key={selectedEventId}
                  event={selectedEvent}
                  onClose={() => {
                    setSelectedEventId(null);
                    setSelectedEvent(null);
                  }}
                />
              )}
            </div>
            {viewAccessTabs && (
              <AlertTabs
                // Keyed on the query so a notification click while already on
                // this alert (history.replace, new ?tab=&refresh=) re-seeds the
                // active tab — AlertTabs reads defaultActiveKey only on mount.
                key={locationSearch}
                defaultActiveKey={spatialTab}
                alertId={alertId}
                module={props.module}
                // CM hands the resolved customer id + context straight down
                // (AlertDetails.js:407) — on the CM route the redux party-key
                // slices are empty (FIN-12019), so this prop is the only source.
                customerId={props.customerId}
                isFromCM={props.isFromCM}
                // Classic's AlertHistoryPrevention takes this as a prop
                // rather than selecting it, so the shell hands it down. The
                // getPartyIdentity dispatch on mount above is what fills it.
                alertPartyIdentity={props.alertPartyIdentity}
                relatedCases={relatedCases}
                customTables={customTables}
              />
            )}
          </>
        ) : (
          <SmartAssessment {...props} />
        )}
      </div>
    </div>
  );
};

FramlAlertDetailShell.propTypes = {
  /** 'spatial' | 'classic' — which view to open on. See the useState above. */
  initialViewMode: PropTypes.oneOf(['spatial', 'classic']),
  location: PropTypes.object,
  match: PropTypes.object,
  // Router history, from withRouter on FramlAlertDetailRouter. Used only by the
  // FP header's back link. NOTE: distinct from the `history` MOCK FIXTURE this
  // component passes DOWN to AlertTabs — same word, unrelated things.
  history: PropTypes.object,
  alertId: PropTypes.string,
  module: PropTypes.string,
  customerId: PropTypes.string,
  isFromCM: PropTypes.bool,
  getPartyIdentity: PropTypes.func.isRequired,
  getAlertDetails: PropTypes.func.isRequired,
  // Live alert-detail response backing the header card.
  alertDetails: PropTypes.object,
  updateQualityResult: PropTypes.object,
  updateQualityError: PropTypes.object,
  updateAlertQuality: PropTypes.func.isRequired,
  savedAlertQuality: PropTypes.object,
  alertPartyIdentity: PropTypes.object,
  // Auth state — drives the tab-strip access gate.
  authentication: PropTypes.object,
  // Live Status/Assign/Note controls injected by CM AlertDetails (alertControls).
  alertMetaSuffix: PropTypes.node
};

// react-redux is 5.x here (no hooks), and the codebase uses connect() throughout.
// Same slice Classic's header reads (SmartAssessmentPageHeader/index.js:110-123),
// populated by the getAMLSCommonAlertDetails dispatched on mount above.
const mapStateToProps = (state) => ({
  alertDetails: state.amlsCommon?.alertDetails?.data,
  // The RESPONSE, not the loading flag — see the note on savingQuality above.
  updateQualityResult: state.amlsCommon?.updateUnifiedAlertStatus?.data,
  savedAlertQuality: state.amlsCommon?.savedAlertQuality,
  updateQualityError: state.amlsCommon?.updateUnifiedAlertStatus?.error,
  // Same slice and default Classic reads (SmartAssessment.js:192). Classic's
  // AlertHistoryPrevention wants the WHOLE object — it reads `.isLoading` as
  // well as `.data` — so this is not `?.data` like the line above.
  alertPartyIdentity: state.amlsCommon?.alertPartyIdentity || {},
  // Same slice Classic reads for its access check (SmartAssessment.js:195).
  authentication: state.authentication
});

const mapDispatchToProps = (dispatch) => ({
  getPartyIdentity: (payload) =>
    dispatch(getAMLSCommonAlertPartyIdentity(payload)),
  getAlertDetails: (payload) => dispatch(getAMLSCommonAlertDetails(payload)),
  updateAlertQuality: (payload) =>
    dispatch(putAMLSCommonAlertPartyUpdate(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FramlAlertDetailShell);
